import * as React from "react"
import { NetworkStatus } from "apollo-client"
import {
  LogsForSiteProvisionJobDocument,
  LogsForSiteProvisionJobQuery,
  LogsForSiteProvisionJobQueryVariables,
  useLogsForSiteProvisionJobQuery,
  useSiteProvisionLogCreatedSubscription,
} from "../../DeployNow/queries.generated"

// This is the limit used on the backend
const RAW_LOGS_LIMIT_PER_PAGE = 500

export function useSiteProvisionLogs(jobId: string, siteId: string) {
  const hasLoadedAll = React.useRef<boolean>(false)

  const {
    data,
    loading,
    error,
    fetchMore,
    networkStatus,
  } = useLogsForSiteProvisionJobQuery({
    skip: !jobId,
    variables: {
      jobId,
      siteId,
      offset: 0,
    },
    fetchPolicy: `cache-and-network`,
  })

  const loadMoreLogs = () => {
    const currentLogsSize = data?.logsForSiteProvisionJob?.length ?? 0
    const hasMoreLogs =
      currentLogsSize >= RAW_LOGS_LIMIT_PER_PAGE && !hasLoadedAll.current
    const isAlreadyFetching = networkStatus === NetworkStatus.fetchMore

    if (!hasMoreLogs || isAlreadyFetching) {
      return
    }

    return fetchMore({
      variables: {
        jobId,
        siteId,
        offset: currentLogsSize,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const moreLogs = fetchMoreResult?.logsForSiteProvisionJob ?? []

        if (moreLogs.length === 0) {
          return previousResult
        }

        return {
          ...previousResult,
          logsForBuild: [
            ...(previousResult?.logsForSiteProvisionJob ?? []),
            ...moreLogs,
          ],
        }
      },
    }).then(({ data }) => {
      const moreLogsSize = data?.logsForSiteProvisionJob?.length ?? 0
      // When the number of returned logs is 500, it means that there _might_ be more logs available
      // Otherwise the number of returned logs is all what is left and everything is loaded
      //
      // Example: if there are 1031 logs total, we will receive those logs in three batches: 500, 500, 31
      // The last batch is lesser than 500, so there couldn't be any more logs to fetch
      hasLoadedAll.current = moreLogsSize < RAW_LOGS_LIMIT_PER_PAGE
    })
  }

  useSiteProvisionLogCreatedSubscription({
    variables: {
      jobId,
    },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const queryInfo = {
        query: LogsForSiteProvisionJobDocument,
        variables: {
          jobId,
          siteId,
        },
      }

      try {
        const currentLogs = client.readQuery<
          LogsForSiteProvisionJobQuery,
          LogsForSiteProvisionJobQueryVariables
        >(queryInfo)

        if (!currentLogs) {
          return
        }

        const currentLogsForProvisioning =
          currentLogs?.logsForSiteProvisionJob || []
        const logsPayload =
          subscriptionData?.data?.siteProvisionLogCreated || []

        currentLogs.logsForSiteProvisionJob = [
          ...currentLogsForProvisioning,
          ...logsPayload,
        ]

        client.writeQuery<
          LogsForSiteProvisionJobQuery,
          LogsForSiteProvisionJobQueryVariables
        >({
          ...queryInfo,
          data: currentLogs,
        })
      } catch (e) {
        console.error(e)
      }
    },
  })

  const provisioningRawLogs = data?.logsForSiteProvisionJob || []

  return [
    provisioningRawLogs,
    { error, loading, loadMoreLogs, networkStatus },
  ] as const
}
